/**
 * <s-cart> manages all cart quantity updates and removals via the command pattern.
 * It fetches partials after each mutation so the cart drawer, cart page, and header
 * counters all stay in sync — do not convert to a full page reload or direct DOM manipulation.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --update-quantity — Adjusts quantity by [data-delta] for [data-line-key]; debounced 200ms per line.
 *   --remove          — Cancels queued/in-flight work for [data-line-key], then sets quantity to 0.
 *   --apply-discount  — Applies the value of the descendant [data-discount-code] input.
 *   --toggle-protection — Adds/removes the shipping protection line ([data-variant-id] / [data-line-key]).
 *
 * Required child markup (queried by selector/attribute):
 *   [data-variant-id]                                — Visual/fallback identifier for a line's row and controls.
 *   [data-line-key]                                  — Primary cart line identity ({{ item.key }}); sent to updateCart as the line id.
 *   [data-delta]                                     — Integer delta (±N) paired with --update-quantity.
 *   [data-qty-min]                                   — Minimum quantity from variant.quantity_rule.min (default 1).
 *   [data-qty-max]                                   — Maximum quantity from variant.quantity_rule.max (when absent, no max).
 *   [data-qty-step]                                  — Increment step from variant.quantity_rule.increment (default 1).
 *   [data-quantity-display][data-line-key]           — Input/element holding the current quantity (value or textContent).
 *   [data-discount-code]                             — Discount-code input read by --apply-discount and Enter.
 *   [style*="view-transition-name"]                  — Row wrapper used for removal focus-restore + view transitions.
 *
 * Element dataset (attributes on <s-cart> itself):
 *   data-partials — Comma-separated partial names to re-fetch (default: "cart-items,cart-count").
 *                   Must match {% partial 'name' %} blocks in cart.liquid / theme.liquid / cart-drawer.liquid.
 *
 * External coupling:
 *   Cart feedback (#sr-announcer + s-notification:add) is owned by the updateCart
 *   handler in standard-actions-override.js — do not duplicate here.
 */
import { partials } from '@shopify/partial-rendering';
import { transition } from '@theme/view-transition';
import { normalizeQuantity, quantityRulesFromDataset } from '@theme/quantity-rules';
import { ViewEventElement } from '@theme/s-view-event';

class CartItems extends ViewEventElement {
  static #pendingFocusStates = new Map();
  static #refreshRevision = 0;

  #lifecycleAc;
  #quantityAcs = new Map();
  #discountAc;
  #noteAc;
  #rafId;
  #debounceTimers = new Map();
  #rollbackValues = new Map();
  #openDetails = new Set();

  static async refreshMountedCarts({ signal } = {}) {
    const revision = ++CartItems.#refreshRevision;
    signal?.throwIfAborted();

    const carts = [...document.querySelectorAll('s-cart')].filter((cart) => cart instanceof CartItems);
    const partialNames = [...new Set(carts.flatMap((cart) => cart.#partialNames))];

    if (!partialNames.length) return;

    // Capture UI state before partial rendering replaces the DOM.
    const focusStates = new Map();
    for (const cart of carts) {
      cart.#saveTransientState();
      focusStates.set(
        cart.id,
        CartItems.#pendingFocusStates.get(cart.id) ?? cart.#refreshFocusState()
      );
    }

    let html;
    try {
      // @partial-fetch-contract attributes=data-partials fallback=cart-items,cart-count
      html = await partials.fetch(...partialNames, { signal });
    } catch (error) {
      signal?.throwIfAborted();
      if (revision !== CartItems.#refreshRevision) return;
      throw error;
    }

    signal?.throwIfAborted();
    if (revision !== CartItems.#refreshRevision) return;

    let applied = false;
    try {
      await transition(() => {
        if (revision !== CartItems.#refreshRevision) return;
        signal?.throwIfAborted();
        partials.apply(html);
        applied = true;
      });
    } catch (error) {
      signal?.throwIfAborted();
      if (revision !== CartItems.#refreshRevision) return;
      throw error;
    }

    signal?.throwIfAborted();
    if (!applied || revision !== CartItems.#refreshRevision) return;

    for (const previousCart of carts) {
      const cart = previousCart.#mountedCart();
      cart.#openDetails = new Set(previousCart.#openDetails);
      cart.#reconcileAfterRefresh();
      const focusState = focusStates.get(previousCart.id);
      cart.#restoreRefreshFocus(focusState);
      if (CartItems.#pendingFocusStates.get(previousCart.id) === focusState) {
        CartItems.#pendingFocusStates.delete(previousCart.id);
      }
    }

    signal?.throwIfAborted();
    CartItems.#normalizePacks();
  }

  connectedCallback() {
    super.connectedCallback();

    this.#lifecycleAc = new AbortController();
    const opts = { signal: this.#lifecycleAc.signal };
    // Shipping protection is always charged in full: drop any code that only discounts it.
    if (this.querySelector('[data-protection-toggle][aria-checked="true"]')) this.#dropProtectionOnlyCodes();

    this.addEventListener('command', (e) => {
      const variantId = e.source.dataset.variantId;
      const lineKey = e.source.dataset.lineKey;

      if (e.command === '--update-quantity' && e.source.dataset.packGroup) {
        // Pack products count in single units: each +/− adds or removes one unit.
        const step = (parseInt(e.source.dataset.delta, 10) || 0) < 0 ? -1 : 1;
        this.#queuePackStep(e.source, step);
        return;
      }

      if (e.command === '--update-quantity') {
        if (!lineKey) return;
        const display = this.#quantityDisplayForLine(lineKey, variantId);
        if (!display) return;
        const current = parseInt(display.value ?? display.textContent, 10) || 0;
        const delta = parseInt(e.source.dataset.delta, 10) || 0;
        const newQuantity = normalizeQuantity(
          current + delta,
          quantityRulesFromDataset(e.source),
          { allowZero: true }
        );

        display.value = newQuantity;
        // Pack lines show total units (pack size × quantity) instead of the pack count.
        for (const units of this.querySelectorAll('[data-units-display]')) {
          if (units.dataset.lineKey !== lineKey) continue;
          units.textContent = (parseInt(units.dataset.packUnits, 10) || 1) * newQuantity;
        }
        const focusTarget = { lineKey, variantId, delta: e.source.dataset.delta };
        if (!this.#debounceTimers.has(lineKey)) this.#rollbackValues.set(lineKey, current);
        clearTimeout(this.#debounceTimers.get(lineKey));
        const rollbackValue = this.#rollbackValues.get(lineKey);
        const packSource = e.source;
        this.#debounceTimers.set(lineKey, setTimeout(() => {
          this.#debounceTimers.delete(lineKey);
          this.#rollbackValues.delete(lineKey);
          const packVariantId = delta > 0 ? this.#packVariantFor(packSource, newQuantity) : null;
          if (packVariantId) {
            this.#swapToPack(lineKey, packVariantId, packSource.dataset.sellingPlanId);
            return;
          }
          this.#updateCart(lineKey, variantId, newQuantity, focusTarget, () => {
            const el = this.#quantityDisplayForLine(lineKey, variantId);
            if (el) el.value = rollbackValue;
          });
        }, 200));
      }

      if (e.command === '--remove') {
        if (!lineKey) return;
        const packButton = e.source.closest('[style*="view-transition-name"]')?.querySelector('button[data-pack-lines]');
        const packLines = packButton?.dataset.packLines || '';
        if (packLines.split(';').filter(Boolean).length > 1) {
          this.#removePackGroup(packLines);
          return;
        }
        this.#updateCart(lineKey, variantId, 0);
      }

      if (e.command === '--upgrade-pack') {
        if (!lineKey) return;
        this.#upgradePack(lineKey, e.source);
      }

      if (e.command === '--toggle-protection') {
        this.#toggleProtection(e.source);
        return;
      }

      if (e.command === '--apply-discount') {
        this.#applyDiscount(this.querySelector('[data-discount-code]')?.value);
      }
    }, opts);

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches?.('[data-discount-code]')) {
        e.preventDefault();
        this.#applyDiscount(e.target.value);
      }
    }, opts);

    this.addEventListener('change', (e) => {
      const input = e.target;

      if (input.matches('[data-quantity-display]')) {
        const variantId = input.dataset.variantId;
        const lineKey = input.dataset.lineKey;
        if (!lineKey) return;
        const parsed = parseInt(input.value, 10);
        const quantity = normalizeQuantity(
          parsed,
          quantityRulesFromDataset(input),
          { allowZero: true }
        );

        // Capture the pre-edit committed value for rollback on mutation failure.
        // defaultValue reflects the server-committed state maintained by #reconcileAfterRefresh.
        const committedValue = input.defaultValue;
        input.value = quantity;
        this.#updateCart(lineKey, variantId, quantity, { lineKey, variantId, input: true }, () => {
          const el = this.#quantityDisplayForLine(lineKey, variantId);
          if (el) el.value = committedValue;
        });
      } else if (input.id === 'cart-note') {
        this.#updateNote(input.value);
      }
    }, opts);

    // Regroup bottles added elsewhere (e.g. from the product page) into the cheapest packs.
    queueMicrotask(() => CartItems.#normalizePacks());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#lifecycleAc?.abort();
    for (const controller of this.#quantityAcs.values()) controller.abort();
    this.#quantityAcs.clear();
    this.#discountAc?.abort();
    this.#noteAc?.abort();
    for (const timer of this.#debounceTimers.values()) clearTimeout(timer);
    this.#debounceTimers.clear();
    this.#rollbackValues.clear();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  #findLineElement(selector, lineKey, variantId = null) {
    for (const el of this.querySelectorAll(selector)) {
      if (lineKey && el.dataset.lineKey === lineKey) return el;
    }

    if (!variantId) return null;
    for (const el of this.querySelectorAll(selector)) {
      if (el.dataset.variantId === variantId) return el;
    }

    return null;
  }

  #quantityDisplayForLine(lineKey, variantId) {
    return this.#findLineElement('[data-quantity-display]', lineKey, variantId);
  }

  #findCartRow(lineKey, variantId) {
    return this.#findLineElement('[data-line-key], [data-variant-id]', lineKey, variantId)
      ?.closest('[style*="view-transition-name"]');
  }

  #mountedCart() {
    if (this.isConnected) return this;

    const replacement = this.id ? this.ownerDocument.getElementById(this.id) : null;
    return replacement instanceof CartItems ? replacement : this;
  }

  #refreshFocusState() {
    const active = this.ownerDocument.activeElement;
    if (!active?.matches || !this.contains(active)) return null;

    if (active.matches('[data-discount-code], button[command="--apply-discount"]')) {
      return { discountTarget: active.matches('[data-discount-code]') ? 'input' : 'button' };
    }

    const lineKey = active.dataset.lineKey;
    const variantId = active.dataset.variantId;
    if (active.matches('button[command="--remove"]')) {
      const row = this.#findCartRow(lineKey, variantId);
      const sibling = row?.nextElementSibling || row?.previousElementSibling;
      const siblingLine = sibling?.querySelector('[data-line-key], [data-variant-id]');
      return {
        isRemoval: true,
        siblingLineKey: siblingLine?.dataset.lineKey,
        siblingVariantId: siblingLine?.dataset.variantId,
      };
    }

    if (active.matches('button[data-delta], [data-quantity-display]')) {
      return {
        focusTarget: {
          lineKey,
          variantId,
          delta: active.dataset.delta,
          input: active.matches('[data-quantity-display]'),
        },
      };
    }

    return null;
  }

  #restoreRefreshFocus(state) {
    if (!state) return;
    const { isRemoval, siblingLineKey, siblingVariantId, focusTarget, discountTarget } = state;

    if (isRemoval) {
      this.#focusAfterRemoval(siblingLineKey, siblingVariantId);
    } else if (discountTarget) {
      const selector = discountTarget === 'input'
        ? '[data-discount-code]'
        : 'button[command="--apply-discount"]';
      this.querySelector(selector)?.focus();
    } else {
      this.#focusTarget(focusTarget)?.focus();
    }
  }

  #focusTarget({ lineKey, variantId, delta, input } = {}) {
    const selector = input ? '[data-quantity-display]' : 'button[data-delta]';
    let variantFallback = null;

    for (const el of this.querySelectorAll(selector)) {
      const deltaMatches = input || delta == null || el.dataset.delta === delta;
      if (!deltaMatches) continue;
      if (lineKey && el.dataset.lineKey === lineKey) return el;
      if (!variantFallback && variantId && el.dataset.variantId === variantId) {
        variantFallback = el;
      }
    }

    return variantFallback;
  }

  #focusAfterRemoval(siblingLineKey, siblingVariantId) {
    const freshRow = siblingLineKey || siblingVariantId
      ? this.#findCartRow(siblingLineKey, siblingVariantId)
      : null;
    const siblingControl = freshRow?.querySelector('a, button');
    if (siblingControl) {
      siblingControl.focus();
      return;
    }

    const anyControl = this.querySelector('a, button');
    if (anyControl) {
      anyControl.focus();
      return;
    }

    const enclosingDialog = this.closest('dialog[open]');
    const closeButton = enclosingDialog?.querySelector('button[command="close"]');
    if (closeButton) {
      closeButton.focus();
      return;
    }

    const main = document.getElementById('main-content') || this.closest('main');
    main?.focus();
  }

  #restoreFocusAfterMutation(focusState) {
    const { isRemoval, siblingLineKey, siblingVariantId, focusTarget } = focusState;
    if (CartItems.#pendingFocusStates.get(this.id) === focusState) {
      CartItems.#pendingFocusStates.delete(this.id);
    }

    cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      const cart = this.#mountedCart();

      if (isRemoval) {
        cart.#focusAfterRemoval(siblingLineKey, siblingVariantId);
      } else if (focusTarget) {
        cart.#focusTarget(focusTarget)?.focus();
      }

      this.#rafId = null;
    });
  }

  #abortQuantityMutation(lineKey) {
    // A queued debounce has no AbortSignal yet, so cancel it explicitly before
    // aborting active work. This ensures an immediate remove or input commit wins.
    clearTimeout(this.#debounceTimers.get(lineKey));
    this.#debounceTimers.delete(lineKey);
    this.#rollbackValues.delete(lineKey);

    const controller = this.#quantityAcs.get(lineKey);
    controller?.abort();
    this.#quantityAcs.delete(lineKey);
  }

  #settleQuantityMutation(lineKey, controller) {
    if (this.#quantityAcs.get(lineKey) !== controller) return;

    this.#quantityAcs.delete(lineKey);
    if (this.#quantityAcs.size === 0) this.removeAttribute('aria-busy');
  }

  get #partialNames() {
    // dataset.partials mirrors the Liquid-written data-partials="..." attribute.
    // The fallback names ('cart-items', 'cart-count') must stay in sync with the
    // {% partial %} block names in cart.liquid and theme.liquid (cart-count) — renaming either breaks cart updates.
    const names = this.dataset.partials;
    return names ? names.split(',').map((s) => s.trim()) : ['cart-items', 'cart-count'];
  }

  #saveTransientState() {
    this.#openDetails.clear();
    for (const details of this.querySelectorAll('details[open]')) {
      const id = details.querySelector('summary')?.textContent.trim();
      if (id) this.#openDetails.add(id);
    }
  }

  #reconcileAfterRefresh() {
    for (const input of this.querySelectorAll('[data-quantity-display]')) {
      if ((input instanceof HTMLInputElement)) {
        // partials.apply() preserves form control state, but cart quantities are
        // server-owned after a cart refresh and should reflect the new markup.
        input.value = input.defaultValue;
      }
    }

    // Restore <details> open state lost during partial rendering
    for (const details of this.querySelectorAll('details')) {
      const id = details.querySelector('summary')?.textContent.trim();
      if (id && this.#openDetails.has(id)) details.open = true;
    }
    this.#openDetails.clear();
  }

  async #updateCart(lineKey, variantId, quantity, focusTarget = null, onRollback = null) {
    // Cancel queued and in-flight work for this line so the latest action wins.
    this.#abortQuantityMutation(lineKey);
    const controller = new AbortController();
    this.#quantityAcs.set(lineKey, controller);
    const { signal } = controller;

    this.setAttribute('aria-busy', 'true');

    const isRemoval = quantity === 0;
    const row = isRemoval ? this.#findCartRow(lineKey, variantId) : null;
    const sibling = row?.nextElementSibling || row?.previousElementSibling;
    const siblingLine = sibling?.querySelector('[data-line-key], [data-variant-id]');
    const siblingLineKey = siblingLine?.dataset.lineKey;
    const siblingVariantId = siblingLine?.dataset.variantId;
    const pendingFocusState = {
      isRemoval,
      siblingLineKey,
      siblingVariantId,
      focusTarget,
    };
    if (this.id) CartItems.#pendingFocusStates.set(this.id, pendingFocusState);

    try {
      // Cart mutation goes through Standard Actions (Storefront API). The shared
      // updateCart override in standard-actions-override.js triggers a partial refresh
      // (cart-items, cart-count, etc.) via CartItems.refreshMountedCarts() after the
      // mutation resolves. The {% partial 'name' %} blocks in cart.liquid, theme.liquid,
      // and snippets/cart-drawer.liquid remain the source of truth for what gets re-rendered.
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ id: lineKey, quantity }] },
        {
          event: { context: 'cart' },
          signal,
        }
      );

      const userError = result?.userErrors?.[0]?.message;
      if (userError) {
        throw new Error(userError, {
          cause: { userErrors: result.userErrors },
        });
      }

      const replacementApplied = signal.aborted && this.#mountedCart() !== this;
      if (signal.aborted && !replacementApplied) return;
      if (!signal.aborted) this.#settleQuantityMutation(lineKey, controller);

      // The Standard Actions override owns feedback: non-toast success uses
      // #sr-announcer, while any toast owns its live role. Avoid a local duplicate.
      this.#restoreFocusAfterMutation(pendingFocusState);
    } catch (error) {
      const replacementApplied = signal.aborted && this.#mountedCart() !== this;
      if (replacementApplied) {
        this.#restoreFocusAfterMutation(pendingFocusState);
        return;
      }
      if (CartItems.#pendingFocusStates.get(this.id) === pendingFocusState) {
        CartItems.#pendingFocusStates.delete(this.id);
      }
      if (signal.aborted) return;
      onRollback?.();
      this.#settleQuantityMutation(lineKey, controller);
      // Cart error toast feedback is owned by the Standard Actions override.
      // Don't duplicate the announcement on [data-cart-status].
    }
  }

  #packQueues = new Map();
  static #packChain = Promise.resolve();
  static #lastNormalized = null;

  /** Runs a cart mutation without refreshing the display when more steps follow. */
  static async #batched(skipRefresh, run) {
    if (!skipRefresh) return run();
    globalThis.__themeCartBatch = (globalThis.__themeCartBatch || 0) + 1;
    try {
      return await run();
    } finally {
      globalThis.__themeCartBatch -= 1;
    }
  }

  /** Parses data-pack-lines ("key:variantId:units:qty;...") into lineKey → line. */
  #packLines(value) {
    const lines = new Map();
    for (const entry of (value || '').split(';')) {
      const parts = entry.split(':');
      if (parts.length < 4) continue;
      const quantity = parseInt(parts.pop(), 10) || 0;
      const units = parseInt(parts.pop(), 10) || 1;
      const variantId = parts.pop();
      const lineKey = parts.join(':');
      lines.set(lineKey, { variantId, units, quantity });
    }
    return lines;
  }

  /** Removes every cart line of a grouped pack row. */
  async #removePackGroup(packLines) {
    const lines = [...this.#packLines(packLines).keys()].map((id) => ({ id, quantity: 0 }));
    if (!lines.length) return;
    this.setAttribute('aria-busy', 'true');
    try {
      await window.Shopify.actions.updateCart({ lines }, { event: { context: 'cart' } });
    } catch (error) {
      // Error feedback is owned by the Standard Actions override.
    } finally {
      this.#mountedCart().removeAttribute('aria-busy');
    }
  }

  /** Debounces unit steps per pack group and shows the new unit count right away. */
  #queuePackStep(source, step) {
    const group = source.dataset.packGroup;
    const queue = this.#packQueues.get(group) ?? {
      delta: 0,
      timer: null,
      map: source.dataset.packMap,
      packLines: source.dataset.packLines,
      sellingPlanId: source.dataset.sellingPlanId,
    };
    queue.delta += step;
    clearTimeout(queue.timer);

    for (const units of this.querySelectorAll('[data-units-display]')) {
      if (units.dataset.lineKey !== source.dataset.lineKey) continue;
      units.textContent = Math.max(0, (parseInt(units.textContent, 10) || 0) + step);
    }

    queue.timer = setTimeout(() => {
      this.#packQueues.delete(group);
      // Serialize pack updates so a quick extra tap waits for the previous one,
      // then reads the cart lines that are actually in the cart now.
      CartItems.#packChain = CartItems.#packChain.then(() => {
        const cart = this.#mountedCart();
        for (const button of cart.querySelectorAll('button[data-pack-group]')) {
          if (button.dataset.packGroup === group) {
            queue.packLines = button.dataset.packLines;
            break;
          }
        }
        return cart.#applyPackTotal(group, queue);
      }).catch(() => {});
    }, 300);
    this.#packQueues.set(group, queue);
  }

  /**
   * New pack combination (variant id → quantity) after `delta` +/− steps:
   * each step adds or removes one bottle, and the new total is always priced
   * as the cheapest mix of packs (3 → 3-pack, 4 → 3-pack + 1, 8 → 5-pack + 3-pack).
   */
  #stepUnits(lines, packs, delta) {
    let total = delta;
    for (const line of lines.values()) total += line.units * line.quantity;
    return CartItems.#cheapestPacks(packs, Math.max(0, total));
  }

  /** Cheapest exact combination of packs for `total` bottles (fewest lines on ties). */
  static #cheapestPacks(packs, total) {
    const result = new Map();
    if (total <= 0) return result;
    const usable = packs.filter((pack) => pack.units > 0 && Number.isFinite(pack.price));
    const cost = new Array(total + 1).fill(Infinity);
    const count = new Array(total + 1).fill(Infinity);
    const choice = new Array(total + 1).fill(null);
    cost[0] = 0;
    count[0] = 0;
    for (let n = 1; n <= total; n += 1) {
      for (const pack of usable) {
        if (pack.units > n || cost[n - pack.units] === Infinity) continue;
        const nextCost = cost[n - pack.units] + pack.price;
        const nextCount = count[n - pack.units] + 1;
        if (nextCost < cost[n] || (nextCost === cost[n] && nextCount < count[n])) {
          cost[n] = nextCost;
          count[n] = nextCount;
          choice[n] = pack;
        }
      }
    }
    if (!choice[total]) return null;
    for (let n = total; n > 0; n -= choice[n].units) {
      const pack = choice[n];
      result.set(pack.id, (result.get(pack.id) || 0) + 1);
      if (!pack.units) break;
    }
    return result;
  }

  /** Parses data-pack-map ("units:id:price,...") into pack entries with string ids. */
  static #parsePackMap(value) {
    return (value || '').split(',').filter(Boolean).map((entry) => {
      const [units, id, price] = entry.split(':');
      return { units: parseInt(units, 10), id: String(id), price: parseInt(price, 10) };
    }).filter((pack) => pack.units > 0 && pack.id);
  }

  /**
   * After any cart render, regroups bottles added elsewhere (e.g. 3 singles from the
   * product page) into the cheapest pack mix. One-time purchases only; runs in the
   * shared pack queue and never repeats for the same cart state.
   */
  static #normalizePacks() {
    if (globalThis.__themeCartBatch > 0) return;
    const maps = new Map();
    for (const button of document.querySelectorAll('s-cart button[data-pack-group][data-pack-map]')) {
      if (button.dataset.sellingPlanId) continue;
      maps.set(button.dataset.packGroup, button.dataset.packMap);
    }
    if (!maps.size) return;
    CartItems.#packChain = CartItems.#packChain.then(async () => {
      const cart = await CartItems.#fetchCart();
      const signature = (cart.items || []).map((item) => `${item.key}:${item.quantity}`).join('|');
      if (signature === CartItems.#lastNormalized) return;
      CartItems.#lastNormalized = signature;
      const changes = {};
      for (const map of maps.values()) {
        const packs = CartItems.#parsePackMap(map);
        const unitsById = new Map(packs.map((pack) => [pack.id, pack.units]));
        const current = new Map();
        const keys = [];
        let total = 0;
        for (const item of cart.items || []) {
          if (item.selling_plan_allocation) continue;
          const units = unitsById.get(String(item.variant_id));
          if (!units) continue;
          total += units * item.quantity;
          keys.push(item);
          current.set(String(item.variant_id), (current.get(String(item.variant_id)) || 0) + item.quantity);
        }
        const desired = CartItems.#cheapestPacks(packs, total);
        if (!desired) continue;
        const same = desired.size === current.size
          && [...desired].every(([id, qty]) => current.get(id) === qty);
        if (same) continue;
        const seen = new Set();
        for (const item of keys) {
          const id = String(item.variant_id);
          const target = seen.has(id) ? 0 : (desired.get(id) || 0);
          seen.add(id);
          if (target !== item.quantity) changes[item.key] = target;
        }
        for (const [id, qty] of desired) {
          if (!seen.has(id) && qty > 0) changes[id] = qty;
        }
      }
      if (!Object.keys(changes).length) return;
      await CartItems.#postCartUpdates(changes);
      await CartItems.refreshMountedCarts();
    }).catch(() => {});
  }


  /** Reads the live cart so pack changes never work from a stale display. */
  static async #fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
    return response.json();
  }

  /** Applies line-key / variant-id quantity changes in one request. */
  static async #postCartUpdates(updates) {
    if (!Object.keys(updates).length) return;
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
  }

  /** Rebuilds a pack group's cart lines for its new total unit count. */
  async #applyPackTotal(group, queue) {
    const packs = (queue.map || '').split(',').filter(Boolean).map((entry) => {
      const [units, id, price] = entry.split(':');
      return { units: parseInt(units, 10), id, price: parseInt(price, 10) };
    }).filter((pack) => pack.units > 0 && pack.id);

    this.setAttribute('aria-busy', 'true');

    // One-time purchases: start from the real cart and apply every change in a
    // single request, so fast or mixed taps always land on the right packs.
    if (!queue.sellingPlanId) {
      try {
        const cart = await CartItems.#fetchCart();
        const unitsById = new Map(packs.map((pack) => [String(pack.id), pack.units]));
        const lines = new Map();
        for (const item of cart.items || []) {
          if (item.selling_plan_allocation) continue;
          const units = unitsById.get(String(item.variant_id));
          if (!units) continue;
          lines.set(item.key, { variantId: String(item.variant_id), units, quantity: item.quantity });
        }
        const desired = this.#stepUnits(lines, packs.map((pack) => ({ ...pack, id: String(pack.id) })), queue.delta);
        if (desired) {
          const changes = {};
          const seen = new Set();
          for (const [lineKey, line] of lines) {
            const target = seen.has(line.variantId) ? 0 : (desired.get(line.variantId) || 0);
            seen.add(line.variantId);
            if (target !== line.quantity) changes[lineKey] = target;
          }
          for (const [variantId, quantity] of desired) {
            if (!seen.has(variantId) && quantity > 0) changes[variantId] = quantity;
          }
          await CartItems.#postCartUpdates(changes);
        }
      } catch (error) {
        // The refresh below shows the real cart.
      } finally {
        await CartItems.refreshMountedCarts().catch(() => {});
        this.#mountedCart().removeAttribute('aria-busy');
      }
      return;
    }

    const lines = this.#packLines(queue.packLines);
    const desired = this.#stepUnits(lines, packs, queue.delta);
    if (!desired) {
      await CartItems.refreshMountedCarts().catch(() => {});
      this.#mountedCart().removeAttribute('aria-busy');
      return;
    }

    const updates = [];
    const seenVariants = new Set();
    for (const [lineKey, line] of lines) {
      const target = seenVariants.has(line.variantId) ? 0 : (desired.get(line.variantId) || 0);
      seenVariants.add(line.variantId);
      if (target !== line.quantity) updates.push({ id: lineKey, quantity: target });
    }
    const adds = [];
    for (const [variantId, quantity] of desired) {
      if (seenVariants.has(variantId) || quantity <= 0) continue;
      const add = { merchandiseId: variantId, quantity };
      if (queue.sellingPlanId) add.sellingPlanId = queue.sellingPlanId;
      adds.push(add);
    }

    try {
      if (adds.length) {
        const added = await CartItems.#batched(updates.length > 0, () => window.Shopify.actions.updateCart({ lines: adds }, { event: { context: 'cart' } }));
        if (added?.userErrors?.length) throw new Error(added.userErrors[0].message);
      }
      if (updates.length) {
        await window.Shopify.actions.updateCart({ lines: updates }, { event: { context: 'cart' } });
      }
      if (!adds.length && !updates.length) await CartItems.refreshMountedCarts();
    } catch (error) {
      // Error feedback is owned by the Standard Actions override; resync the display.
      await CartItems.refreshMountedCarts().catch(() => {});
    } finally {
      this.#mountedCart().removeAttribute('aria-busy');
    }
  }

  /**
   * Returns the pack variant id whose unit count equals the line's total units
   * (pack units × quantity), from the + button's data-pack-map ("3:id,5:id,").
   */
  #packVariantFor(source, quantity) {
    const map = source?.dataset.packMap;
    const units = parseInt(source?.dataset.packUnits, 10);
    if (!map || !units) return null;
    const totalUnits = units * quantity;
    for (const entry of map.split(',')) {
      const [packUnits, variantId] = entry.split(':');
      if (variantId && parseInt(packUnits, 10) === totalUnits) return variantId;
    }
    return null;
  }

  /** Replaces a line with one unit of the pack variant (keeps selling plan). */
  async #swapToPack(lineKey, merchandiseId, sellingPlanId) {
    const line = { merchandiseId, quantity: 1 };
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    this.#abortQuantityMutation(lineKey);
    this.setAttribute('aria-busy', 'true');
    try {
      const added = await CartItems.#batched(true, () => window.Shopify.actions.updateCart(
        { lines: [line] },
        { event: { context: 'cart' } }
      ));
      if (added?.userErrors?.length) throw new Error(added.userErrors[0].message);
      await window.Shopify.actions.updateCart(
        { lines: [{ id: lineKey, quantity: 0 }] },
        { event: { context: 'cart' } }
      );
    } catch (error) {
      // Error feedback is owned by the Standard Actions override; restore the shown quantity.
      const cart = this.#mountedCart();
      const display = cart.#quantityDisplayForLine(lineKey, null);
      if (display && display instanceof HTMLInputElement) display.value = display.defaultValue;
    } finally {
      this.#mountedCart().removeAttribute('aria-busy');
    }
  }

  /**
   * Swaps a cart line for a bigger pack variant: adds the new variant (same
   * quantity and selling plan), then removes the original line.
   */
  async #upgradePack(lineKey, source) {
    const merchandiseId = source.dataset.upgradeVariantId;
    if (!merchandiseId) return;
    const quantity = parseInt(source.dataset.upgradeQuantity, 10) || 1;
    const line = { merchandiseId, quantity };
    if (source.dataset.sellingPlanId) line.sellingPlanId = source.dataset.sellingPlanId;

    this.#abortQuantityMutation(lineKey);
    source.disabled = true;
    this.setAttribute('aria-busy', 'true');
    const removeKeys = (source.dataset.removeLines || '').split(',').filter(Boolean);
    if (!removeKeys.length) removeKeys.push(lineKey);

    // One-time purchases: queue behind any pending +/− change, read the real cart,
    // then swap every line of this product for the pack in a single request.
    if (!line.sellingPlanId) {
      // Pending +/− taps for this product are superseded by the upgrade.
      for (const [group, pending] of this.#packQueues) {
        clearTimeout(pending.timer);
        this.#packQueues.delete(group);
      }
      const productId = source.dataset.productId;
      CartItems.#packChain = CartItems.#packChain.then(async () => {
        try {
          const cart = await CartItems.#fetchCart();
          const updates = {};
          let packLineKey = null;
          for (const item of cart.items || []) {
            if (item.selling_plan_allocation) continue;
            const sameProduct = productId ? String(item.product_id) === productId : removeKeys.includes(item.key);
            if (!sameProduct) continue;
            if (!packLineKey && String(item.variant_id) === String(merchandiseId)) {
              packLineKey = item.key;
              updates[item.key] = quantity;
            } else {
              updates[item.key] = 0;
            }
          }
          if (!packLineKey) updates[merchandiseId] = quantity;
          await CartItems.#postCartUpdates(updates);
        } catch (error) {
          if (source.isConnected) source.disabled = false;
        } finally {
          await CartItems.refreshMountedCarts().catch(() => {});
          this.#mountedCart().removeAttribute('aria-busy');
        }
      }).catch(() => {});
      return;
    }

    try {
      const added = await CartItems.#batched(true, () => window.Shopify.actions.updateCart(
        { lines: [line] },
        { event: { context: 'cart' } }
      ));
      if (added?.userErrors?.length) throw new Error(added.userErrors[0].message);
      // Replace every line of the pack group (falls back to the clicked line).
      await window.Shopify.actions.updateCart(
        { lines: removeKeys.map((id) => ({ id, quantity: 0 })) },
        { event: { context: 'cart' } }
      );
    } catch (error) {
      // Error feedback is owned by the Standard Actions override.
      if (source.isConnected) source.disabled = false;
    } finally {
      this.#mountedCart().removeAttribute('aria-busy');
    }
  }

  /**
   * Shipping protection must always add its full price. A discount code whose only
   * saving lands on the protection line (e.g. a one-time-only code in a subscription
   * cart) is removed so protection is charged; codes that save on real products stay.
   * Shared across <s-cart> instances so it runs once at a time.
   */
  static #protectionCheck = null;
  #dropProtectionOnlyCodes() {
    if (CartItems.#protectionCheck) return CartItems.#protectionCheck;
    const toggle = document.querySelector('[data-protection-toggle][data-product-id]');
    const protectionId = Number(toggle?.dataset.productId);
    if (!protectionId) return Promise.resolve();
    CartItems.#protectionCheck = (async () => {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' });
        const cart = await response.json();
        const codes = (cart.discount_codes || []).filter((c) => c.applicable).map((c) => c.code);
        if (codes.length === 0) return;
        const keep = codes.filter((code) => {
          let onProtection = 0;
          let elsewhere = 0;
          for (const item of cart.items || []) {
            for (const d of item.discounts || []) {
              if (String(d.title).toLowerCase() !== code.toLowerCase()) continue;
              if (item.product_id === protectionId) onProtection += d.amount;
              else elsewhere += d.amount;
            }
          }
          return !(onProtection > 0 && elsewhere === 0);
        });
        if (keep.length === codes.length) return;
        await window.Shopify.actions.updateCart({ discountCodes: keep }, { event: { context: 'cart' } });
      } catch (error) {
        /* leave the cart as is */
      } finally {
        CartItems.#protectionCheck = null;
      }
    })();
    return CartItems.#protectionCheck;
  }

  /**
   * Adds or removes the optional shipping protection line. A line key on the
   * switch means protection is in the cart (remove it); otherwise add one unit.
   */
  async #toggleProtection(source) {
    const lineKey = source.dataset.lineKey;
    const variantId = source.dataset.variantId;
    if (!lineKey && !variantId) return;
    const lines = lineKey
      ? [{ id: lineKey, quantity: 0 }]
      : [{ merchandiseId: variantId, quantity: 1 }];
    // With a subscription in the cart, protection joins the same plan so it renews with every delivery.
    if (!lineKey && source.dataset.sellingPlanId) lines[0].sellingPlanId = source.dataset.sellingPlanId;

    if (lineKey) this.#abortQuantityMutation(lineKey);
    source.setAttribute('aria-checked', String(!lineKey));
    source.disabled = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const result = await window.Shopify.actions.updateCart({ lines }, { event: { context: 'cart' } });
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
      if (!lineKey) await this.#dropProtectionOnlyCodes();
    } catch (error) {
      // Error feedback is owned by the Standard Actions override.
      if (source.isConnected) {
        source.setAttribute('aria-checked', String(Boolean(lineKey)));
        source.disabled = false;
      }
    } finally {
      this.#mountedCart().removeAttribute('aria-busy');
    }
  }

  async #applyDiscount(code) {
    code = code?.trim();
    if (!code) return;

    this.#discountAc?.abort();
    this.#discountAc = new AbortController();
    const { signal } = this.#discountAc;

    this.setAttribute('aria-busy', 'true');
    try {
      const result = await window.Shopify.actions.updateCart(
        { discountCodes: [code] },
        { event: { context: 'cart' }, signal }
      );
      const userError = result?.userErrors?.[0]?.message;
      if (userError) throw new Error(userError, { cause: { userErrors: result.userErrors } });
      if (signal.aborted) return;
      await this.#dropProtectionOnlyCodes();
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    }
  }

  async #updateNote(note) {
    this.#noteAc?.abort();
    this.#noteAc = new AbortController();
    const { signal } = this.#noteAc;

    this.setAttribute('aria-busy', 'true');
    try {
      await window.Shopify.actions.updateCart(
        { note },
        { event: { context: 'cart' }, signal }
      );
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    }
  }

}

if (!customElements.get('s-cart')) {
  customElements.define('s-cart', CartItems);
}
